import { Spinner } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { InfrastructureFeaturesFetcher } from './InfrastructureFeaturesFetcher';
import { MapBoundsUpdater } from './MapBoundsUpdater';
import { MapFeaturesFetcher } from './MapFeaturesFetcher';
import { MapLayers } from './MapLayers';
import { MapMarginManager } from './MapMarginManager';
import { MAP_DEFAULT_OPTIONS, MAP_DEFAULT_STATE } from './constants';
import { IMapContext, MapContext } from './context';
import * as styles from './styles.css';
import { useTrackMapInteraction } from './utils/useTrackMapInteraction';
import { MODULES } from '../../../common/constants/map';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { selectIsResultsLoading, selectResultsStatus } from '../../../common/selectors/results';
import { loadYMapsApi } from '../../../common/utils/loadYMapsApi';
import { selectIsInfrastructureOnMapEnabled, selectMapBounds } from '../../selectors';

enum EMapStatus {
  Loading = 'Loading',
  Succeed = 'Succeed',
  Failed = 'Failed',
}

export function Map() {
  const applicationContext = useApplicationContext();
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isResultsLoading = useSelector(selectIsResultsLoading);
  const resultsStatus = useSelector(selectResultsStatus);
  const initialBounds = useSelector(selectMapBounds);
  const isInfrastructureEnabled = useSelector(selectIsInfrastructureOnMapEnabled);
  const trackMapInteraction = useTrackMapInteraction();
  const handleMapInteractions = React.useCallback(() => trackMapInteraction(), [trackMapInteraction]);
  const options = applicationContext.config.get<YMaps.IMapOptions>(
    'dailyrentSearchFrontend.ymapsMapConstructorOptions',
  );

  const [, setStatus] = React.useState<EMapStatus>(EMapStatus.Loading);
  const [ymaps, setYMaps] = React.useState<YMaps.IYMaps>();
  const [map, setMap] = React.useState<YMaps.Map>();

  React.useEffect(() => {
    try {
      loadYMapsApi(MODULES).then(setYMaps);
    } catch (e) {
      setStatus(EMapStatus.Failed);
    }
  }, []);

  React.useEffect(() => {
    if (!ymaps) {
      return;
    }

    const map = new ymaps.Map(
      elementRef.current as HTMLDivElement,
      { ...MAP_DEFAULT_STATE, ...initialBounds },
      { ...MAP_DEFAULT_OPTIONS, ...options },
    );

    setMap(map);
    setStatus(EMapStatus.Succeed);
  }, [ymaps]);

  const context = React.useMemo(() => ({ ymaps, map }), [map, ymaps]);

  const isMapReady = !!context.ymaps && !!context.map;

  return (
    <>
      <div
        ref={elementRef}
        className={styles['map']}
        onClick={handleMapInteractions}
        onDragStart={handleMapInteractions}
        onTouchStart={handleMapInteractions}
        /** На эти атрибуты смотрят webui тесты */
        data-clusters-status={resultsStatus}
        data-results-status={resultsStatus}
        data-name="Map"
        data-testid="Map"
      />
      {isMapReady && (
        <MapContext.Provider value={context as IMapContext}>
          <MapBoundsUpdater />
          <MapFeaturesFetcher />
          {isInfrastructureEnabled && <InfrastructureFeaturesFetcher />}
          <MapLayers />
          <MapMarginManager />
        </MapContext.Provider>
      )}
      {isResultsLoading && (
        <div className={styles['spinner-container']}>
          <Spinner size={20} />
        </div>
      )}
    </>
  );
}
